import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Play, Clock, MapPin } from 'lucide-react';
import { useLiveGames } from '@/hooks/useLiveGames';
import { Link } from 'react-router-dom';

interface LiveGamesSectionProps {
  stateProvince: string;
  country?: string;
  sportFilter?: string;
}

const LiveGamesSection: React.FC<LiveGamesSectionProps> = ({
  stateProvince,
  country = 'United States',
  sportFilter,
}) => {
  const { games, inProgressGames, upcomingGames, loading, error } = useLiveGames({
    stateProvince,
    country,
    sportFilter,
  });

  if (loading) {
    return (
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-foreground">
            <Play className="h-5 w-5 text-primary" />
            Games Being Played Now
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-20 w-full rounded-lg" />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-foreground">
            <Play className="h-5 w-5 text-primary" />
            Games Being Played Now
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">Unable to load games. Please try again later.</p>
        </CardContent>
      </Card>
    );
  }

  const hasGames = games.length > 0;

  return (
    <Card className="bg-card border-border">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-foreground">
          <Play className="h-5 w-5 text-primary" />
          Games Being Played Now
          {inProgressGames.length > 0 && (
            <Badge variant="destructive" className="ml-2 animate-pulse">
              LIVE ({inProgressGames.length})
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!hasGames ? (
          <div className="text-center py-8">
            <p className="text-muted-foreground">No games scheduled for today in {stateProvince}.</p>
            <p className="text-sm text-muted-foreground mt-1">Check back later or explore other states!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {/* In Progress Games */}
            {inProgressGames.map(game => (
              <Link 
                key={game.id} 
                to={`/games/${game.id}/details`}
                className="block"
              >
                <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-lg hover:bg-destructive/20 transition-colors">
                  <div className="flex items-center justify-between mb-2">
                    <Badge variant="destructive" className="animate-pulse">🔴 LIVE</Badge>
                    {game.sport_name && (
                      <Badge variant="secondary">{game.sport_name}</Badge>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="font-semibold text-foreground">{game.home_team_name}</p>
                      <p className="text-muted-foreground">vs</p>
                      <p className="font-semibold text-foreground">{game.away_team_name}</p>
                    </div>
                    {(game.home_score !== null || game.away_score !== null) && (
                      <div className="text-right">
                        <p className="text-2xl font-bold text-foreground">{game.home_score || 0}</p>
                        <p className="text-muted-foreground">-</p>
                        <p className="text-2xl font-bold text-foreground">{game.away_score || 0}</p>
                      </div>
                    )}
                  </div>
                  {game.venue_name && (
                    <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                      <MapPin className="h-3 w-3" />
                      {game.venue_name}
                    </div>
                  )}
                </div>
              </Link>
            ))}

            {/* Upcoming Games */}
            {upcomingGames.map(game => (
              <Link 
                key={game.id} 
                to={`/games/${game.id}/details`}
                className="block"
              >
                <div className="p-4 bg-muted/50 rounded-lg hover:bg-muted transition-colors">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">
                        {game.game_time || 'TBD'}
                      </span>
                    </div>
                    {game.sport_name && (
                      <Badge variant="outline">{game.sport_name}</Badge>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-foreground">{game.home_team_name}</p>
                      <p className="text-sm text-muted-foreground">vs {game.away_team_name}</p>
                    </div>
                  </div>
                  {game.venue_name && (
                    <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                      <MapPin className="h-3 w-3" />
                      {game.venue_name}
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default LiveGamesSection;
